import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { login } from '@kss/auth';
import { authService } from '@kss/api';
import { Button, useToast, cn, FieldError, useField, v, onlyDigits, applyGuard, getTable } from '@kss/ui';

/**
 * "Customer Information" capture — Name + Mobile → OTP, shown when a not-signed-in guest
 * tries to add to their order / place an order (mirrors the Flutter Petpooja flow). Once
 * they verify, they're signed in (redux `login`) and it never shows again for the session.
 *
 * Kept separate from the full-page /login so the guest can browse the menu freely and only
 * gets asked for their details at the moment of ordering.
 */
export default function CustomerInfoModal({
  open,
  onClose,
  onSuccess,
  initialName = '',
  initialPhone = '',
  skipOtp = false
}: {
  open: boolean;
  onClose: () => void;
  /** Called once signed in, with the freshly-verified name/phone (redux may not have
   *  propagated to the caller's `user` yet, so the values are passed through explicitly). */
  onSuccess: (customer: { name: string; phone?: string }) => void;
  /** Pre-fill for a signed-in customer completing a missing name (their phone is already known). */
  initialName?: string;
  initialPhone?: string;
  /** QR-scanner ordering only: skip OTP verification and sign in from name + mobile alone. Every
   *  other entry point (takeaway, full /login) keeps the OTP step. */
  skipOtp?: boolean;
}) {
  const [step, setStep] = useState<'info' | 'otp'>('info');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [devCode, setDevCode] = useState('');
  const [flash, setFlash] = useState(false);
  const [busy, setBusy] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const dispatch = useDispatch();
  const toast = useToast();

  const digits = phone.replace(/\D/g, '');

  // The same two rules the /login page and the API apply — this modal writes the customer record
  // that every later order and bill is scoped to, so a bad number here is a bill nobody can find.
  const nameField = useField(name, [
    v.required('Name'),
    v.minLength(2, 'Name'),
    v.maxLength(60, 'Name'),
    v.safeName('Name'),
    ...v.safeText('Name')
  ]);
  const phoneField = useField(phone, [v.required('Mobile number'), v.mobile('Mobile number')]);
  const infoValid = nameField.isValid && phoneField.isValid;

  // Reset to a clean state whenever the sheet is (re)opened, pre-filling any known name/phone.
  useEffect(() => {
    if (open) {
      setStep('info');
      setName(initialName ?? '');
      setPhone((initialPhone ?? '').replace(/\D/g, ''));
      setOtp(['', '', '', '', '', '']);
      setDevCode('');
      setBusy(false);
      // Clear touched too, so reopening a blank sheet doesn't greet the user with errors they
      // haven't earned yet.
      nameField.resetTouched();
      phoneField.resetTouched();
    }
    // The field helpers are recreated each render; including them would re-run this on every
    // keystroke and wipe what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialName, initialPhone]);

  if (!open) return null;

  /**
   * QR/scan ordering flow: OTP verification is DISABLED — a guest who scans the table QR is signed
   * in directly from name + mobile, with no code to enter. The number is still registered
   * server-side (so orders/bills stay scoped to them), but the two-step OTP screen is skipped.
   *
   * The TABLE is what stands in for the code: `POST /api/auth/guest` only issues a token if the
   * scanned table resolves and is enabled.
   *
   * This used to call send-otp, read `devCode` out of the response and post it straight back to
   * verify-otp, falling back to a hardcoded '102030'. That only worked while the API handed the
   * OTP to the caller. Once the code became random and private, `devCode` was always empty, the
   * literal was always wrong, and every scan-and-order ended on "Could not sign you in, try again".
   *
   * To RE-ENABLE OTP: point the button back at `sendOtp` (below) instead of `submitInfo`.
   */
  async function submitInfo() {
    nameField.markTouched();
    phoneField.markTouched();
    if (!infoValid) return; // the messages are now under the fields themselves
    setBusy(true);
    try {
      const auth = await authService.guest(getTable(), name.trim(), digits);
      dispatch(
        login({
          user: { id: auth.id, name: auth.name, initials: auth.initials, phone: auth.phone, tier: auth.tier, points: auth.points },
          session: { token: auth.token, tenantId: 'thalios' }
        })
      );
      toast.show('Signed in', 'success');
      onSuccess({ name: auth.name, phone: auth.phone });
    } catch (err) {
      // "That table isn't available…" is actionable; "Could not sign you in" is not — and an
      // unknown or disabled table is the most likely reason this call fails.
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.show(message?.trim() || 'Could not sign you in, try again', 'error');
      setBusy(false);
    }
  }

  // OTP send step — kept for when verification is re-enabled (currently bypassed by submitInfo).
  async function sendOtp() {
    nameField.markTouched();
    phoneField.markTouched();
    if (!infoValid) return; // the messages are now under the fields themselves
    setBusy(true);
    try {
      // The code is deliberately not read, shown or auto-filled — see the note in
      // pages/Login.tsx. Displaying it made the OTP step verify nothing.
      await authService.sendOtp(digits);
      setOtp(['', '', '', '', '', '']);
      setStep('otp');
      toast.show('Code sent to your mobile', 'info');
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch {
      toast.show('Could not send the code, try again', 'error');
    } finally {
      setBusy(false);
    }
  }

  function onOtpChange(i: number, val: string) {
    const d = val.replace(/\D/g, '').slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 5) otpRefs.current[i + 1]?.focus();
  }

  function onOtpKey(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  }

  async function verify() {
    const code = otp.join('');
    // Six boxes are shown and the API issues a six-digit code, so accepting four was letting a
    // half-entered code through to a guaranteed server rejection.
    if (code.length < 6) {
      toast.show('Enter the 6-digit code', 'error');
      return;
    }
    setBusy(true);
    try {
      const auth = await authService.verifyOtp(digits, code, name.trim());
      dispatch(
        login({
          user: {
            id: auth.id,
            name: auth.name,
            initials: auth.initials,
            phone: auth.phone,
            tier: auth.tier,
            points: auth.points
          },
          session: { token: auth.token, tenantId: 'thalios' }
        })
      );
      toast.show('Signed in', 'success');
      onSuccess({ name: auth.name, phone: auth.phone });
    } catch {
      toast.show('Invalid or expired code', 'error');
      setBusy(false);
    }
  }

  return (
    <div
      className='fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center'
      onClick={onClose}
    >
      <div
        className='w-full max-w-[400px] rounded-t-2xl bg-surface-card p-5 shadow-pop sm:rounded-2xl'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between'>
          <div className='font-display text-base font-bold'>
            {step === 'info' ? 'Customer Information' : 'Verify your number'}
          </div>
          <button onClick={onClose} className='text-lg leading-none text-content-muted'>
            ✕
          </button>
        </div>

        {step === 'info' ? (
          <div className='mt-4 flex flex-col gap-3'>
            <div className='mt-0.5 text-[11.5px] text-content-muted'>
              Add your name and mobile to place your order — you’ll only need to do this once.
            </div>
            <div>
              <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
                Name<span className='text-danger'> *</span>
              </div>
              <input
                value={name}
                onChange={(e) => setName(applyGuard(e.target.value, 'name', 60))}
                onBlur={nameField.onBlur}
                autoFocus
                maxLength={60}
                placeholder='Enter name'
                aria-label='Name'
                aria-invalid={nameField.error ? true : undefined}
                className={cn(
                  'w-full rounded-xl border-[1.5px] px-4 py-3 text-sm font-medium focus:outline-none',
                  nameField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                )}
              />
              <FieldError error={nameField.error} />
            </div>
            <div>
              <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
                Mobile number<span className='text-danger'> *</span>
              </div>
              <div
                className={cn(
                  'flex items-center gap-3 rounded-xl border-[1.5px] px-4 py-3',
                  phoneField.error ? 'border-danger' : 'border-border focus-within:border-primary'
                )}
              >
                <span className='font-display text-sm font-bold'>+91</span>
                <span className='h-4 w-px bg-border' />
                <input
                  value={phone}
                  onChange={(e) => setPhone(onlyDigits(e.target.value).slice(0, 10))}
                  onBlur={phoneField.onBlur}
                  inputMode='numeric'
                  maxLength={10}
                  placeholder='98765 43210'
                  aria-label='Mobile number'
                  aria-invalid={phoneField.error ? true : undefined}
                  className='w-full bg-transparent text-sm font-semibold tracking-wide text-content-primary placeholder:font-normal placeholder:text-content-muted focus:outline-none'
                />
              </div>
              <FieldError error={phoneField.error} />
            </div>
            {/* QR-scanner flow (`skipOtp`) signs in directly from name + mobile; every other entry
                keeps the OTP step. */}
            <Button block size='lg' className='mt-1' onClick={skipOtp ? submitInfo : sendOtp} disabled={busy || !infoValid}>
              {busy ? (skipOtp ? 'Please wait…' : 'Sending…') : skipOtp ? 'Continue →' : 'Send OTP →'}
            </Button>
          </div>
        ) : (
          <div className='mt-4 flex flex-col'>
            <div className='text-[13px] leading-relaxed text-content-secondary'>
              Enter the 6-digit code sent to <b className='text-content-primary'>+91 {digits}</b> ·{' '}
              <button onClick={() => setStep('info')} className='font-semibold text-primary'>
                Change
              </button>
            </div>
            <div className='mt-4 grid grid-cols-6 gap-2'>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  value={d}
                  onChange={(e) => onOtpChange(i, e.target.value)}
                  onKeyDown={(e) => onOtpKey(i, e)}
                  inputMode='numeric'
                  maxLength={1}
                  className={cn(
                    'h-13 w-full rounded-xl border-[1.5px] py-3 text-center font-display text-xl font-bold text-content-primary transition focus:outline-none',
                    d ? 'border-primary' : 'border-border focus:border-primary',
                    flash && 'motion-safe:animate-pulse bg-primary-light ring-2 ring-primary/50'
                  )}
                />
              ))}
            </div>
            <Button block size='lg' className='mt-4' onClick={verify} disabled={busy || otp.join('').length < 6}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
